import React from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Grid, 
  Chip
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { Warning, Info } from '@mui/icons-material';
import { useEmbedded } from '../../hooks/useEmbedded';

const SectionPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),  
}));

const PrerequisiteCard = styled(Box)(({ theme }) => ({
  display: 'flex',
  height: '100%',
  border: `1px solid #bbb`,
  borderRadius: '8px',
  overflow: 'hidden',
}));

const CoursePrerequisites = ({ prerequisites, parentCourseId, parentCourseName }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isEmbedded = useEmbedded();

  // Get the current course ID from the URL path
  const currentCourseId = location.pathname.split('/').pop();

  const getStatusIcon = (prerequisite) => {
    if (!prerequisite.status) {
      return prerequisite.required === '1' ? <Warning color="warning" /> : <Info color="disabled" />;
    }
    
    switch (prerequisite.status) {
      case '0': return <Info color="info" />;
      case '1': return <Warning color="error" />;
      default: return <Info color="info" />;
    }
  };
  
  const getStatusColor = (prerequisite) => {
    if (!prerequisite.status) {
      return prerequisite.required === '1' ? 'warning' : 'default';
    }
    
    switch (prerequisite.status) {
      case '1': return 'success';
      case '2': return 'info';
      default: return 'error';
    }
  };

  return (
    <SectionPaper elevation={0}>
      <Typography variant="h5" component="h2" gutterBottom>
        Prerequisites
      </Typography>
      <Typography variant="body2" color="textSecondary" paragraph>
        The following courses are prerequisites for this course.
      </Typography>
      
      <Grid container spacing={2}>
        {prerequisites.map((prerequisite) => {
          const coverImage = prerequisite.cover_picture 
            ? `/uploads/courses/${prerequisite.cover_picture}`
            : `/assets/images/course-default.jpg`;
            
          return (
            <Grid item xs={12} sm={12} key={prerequisite.id}>
              <PrerequisiteCard
                className="prerequisite-card"
                onClick={() => {
                  // Add embedded path prefix if in embedded mode
                  const pathPrefix = isEmbedded ? '/embed' : '';
                  navigate(`${pathPrefix}/learner/course/${prerequisite.prerequisite_id}`, {
                    state: {
                      parentCourseId: parentCourseId || currentCourseId,
                      parentCourseName: parentCourseName || "Parent Course"
                    }
                  });
                }}
              >
                <Box className="card-image-wrapper">
                  <img
                    className="card-image"
                    src={coverImage}
                    alt={prerequisite.name}
                  />
                </Box>
                <Box className="card-content">                  
                    <Chip 
                      icon={getStatusIcon(prerequisite)}
                      label={prerequisite.status_text}
                      size="small"
                      backgroundColor={getStatusColor(prerequisite)}
                      className="requirement-chip"
                    />
                    <Typography variant="subtitle1" className="card-title"  sx={{ lineHeight: "normal" }}>
                      {prerequisite.name}
                    </Typography>
                    <Typography variant="body2" className="prerequisite-description" 
                      dangerouslySetInnerHTML={{ __html: prerequisite.description}}
                    />
                </Box>
              </PrerequisiteCard>
            </Grid>
          );
        })}
      </Grid>
    </SectionPaper>
  );
};

export default CoursePrerequisites;